import { Paginator, Skeleton } from "components";
import Playlist from "components/Playlist";
import { RefObject } from "react";
import { ProductCardDto, RelatedCategory } from "typing";
import { SpaceWrapper, theme } from "ui";
import { buildTestIds, getProductPosition } from "application";
import {
  AggregatorCustom,
  AlsoSeeContainer,
  AlsoSeeContent,
  ArrowDownIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  ButtonCustom,
  CategoriesItems,
  CategoriesSliderWrapper,
  ItemsTitle,
  ProductSideMenuItem,
  ProductsSideMenu,
  SideMenuItems,
  SkeletonContainer,
  TextCustom,
  Title,
  TitleItem,
} from "./styles";

interface AlsoSeeUIProps {
  idSection: string;
  isBlackTheme: boolean;
  isLoadingRelatedProducts: boolean;
  isLoadingProductCards: boolean;
  productsData: ProductCardDto[];
  relatedCategoryData: RelatedCategory[] | null | undefined;
  isClientTablet: boolean;
  alsoSeeContainerRef: RefObject<HTMLDivElement>;
  isAlsoSeeCollapsed: boolean;
  totalProductCardsRows: number;
  selectedCategoryId: number;
  isToShowPaginator: boolean;
  productIdsListLength: number;
  pageSize: number;
  currentPage: number;
  onToggleShowCollapsible: () => void;
  handleOnClickOnCategoryItem: (categoryId: number) => void;
  onSetCurrentPage: (newCurrentPage: number) => void;
  onScrollToAlsoSeeContainer: () => void;
}

const AlsoSeeUI = ({
  idSection,
  isBlackTheme,
  isLoadingRelatedProducts,
  isLoadingProductCards,
  productsData,
  relatedCategoryData,
  isClientTablet,
  alsoSeeContainerRef,
  isAlsoSeeCollapsed,
  totalProductCardsRows,
  selectedCategoryId,
  isToShowPaginator,
  productIdsListLength,
  pageSize,
  currentPage,
  onToggleShowCollapsible,
  handleOnClickOnCategoryItem,
  onSetCurrentPage,
  onScrollToAlsoSeeContainer,
}: AlsoSeeUIProps) => {
  const arrowColor = isBlackTheme
    ? theme.colors.blackFriday.white
    : theme.colors.primary["200"];

  const indexBased = 1;

  const isLoadingProducts = !relatedCategoryData || isLoadingProductCards;

  return (
    <AlsoSeeContainer
      {...buildTestIds("also-see-container")}
      id={idSection}
      ref={alsoSeeContainerRef}
    >
      {isClientTablet ? (
        <AggregatorCustom>
          <ButtonCustom
            {...buildTestIds("also-see-button")}
            onClick={onToggleShowCollapsible}
            isBlackTheme={isBlackTheme}
          >
            <TextCustom
              {...buildTestIds("also-see-button-text")}
              isBlackTheme={isBlackTheme}
            >
              Veja também
            </TextCustom>
            {isAlsoSeeCollapsed ? (
              <ArrowUpIcon color={arrowColor} />
            ) : (
              <ArrowDownIcon color={arrowColor} />
            )}
          </ButtonCustom>
        </AggregatorCustom>
      ) : (
        <Title isBlackTheme={isBlackTheme}>Veja também</Title>
      )}

      <AlsoSeeContent collapse={isClientTablet && !isAlsoSeeCollapsed}>
        {isClientTablet ? (
          <CategoriesSliderWrapper>
            {relatedCategoryData?.map((item, index) => (
              <CategoriesItems
                isBlackTheme={isBlackTheme}
                isSelected={
                  item?.category ===
                  (selectedCategoryId || relatedCategoryData?.[0]?.category)
                }
                onClick={() => handleOnClickOnCategoryItem(item?.category)}
                key={`${item?.category}#${index + 1}`}
              >
                <ItemsTitle isBlackTheme={isBlackTheme}>
                  {item?.name}
                </ItemsTitle>
              </CategoriesItems>
            ))}
          </CategoriesSliderWrapper>
        ) : (
          <ProductsSideMenu isBlackTheme={isBlackTheme}>
            {relatedCategoryData?.map((item, index) => (
              <ProductSideMenuItem key={`${item?.category}#${index + 1}`}>
                <SideMenuItems
                  isBlackTheme={isBlackTheme}
                  isSelected={
                    item?.category ===
                    (selectedCategoryId || relatedCategoryData?.[0]?.category)
                  }
                  onClick={() => handleOnClickOnCategoryItem(item?.category)}
                >
                  <TitleItem isBlackTheme={isBlackTheme}>
                    {item?.name}
                  </TitleItem>
                  <ArrowRightIcon
                    color={
                      !isBlackTheme && item?.category === selectedCategoryId
                        ? theme.colors.blackFriday.black
                        : arrowColor
                    }
                  />
                </SideMenuItems>
              </ProductSideMenuItem>
            ))}
          </ProductsSideMenu>
        )}

        {isLoadingProductCards || isLoadingRelatedProducts ? (
          <SkeletonContainer>
            <Skeleton
              type="card"
              hasMargin={false}
              count={3}
              height={350}
              width={isClientTablet ? 200 : 250}
            />
          </SkeletonContainer>
        ) : (
          <SpaceWrapper>
            <Playlist
              productCards={[
                ...(productsData?.map((product, index) => ({
                  ...product,
                  index: getProductPosition(index + 1, pageSize, currentPage),
                })) ?? []),
              ]}
            />
            {isToShowPaginator && productIdsListLength > pageSize && (
              <Paginator
                justifyContent="center"
                visiblePages={5}
                pageSize={pageSize}
                totalRows={totalProductCardsRows}
                loading={isLoadingProducts}
                page={currentPage ?? indexBased}
                pageChangeHandler={(newCurrentPage) => {
                  onSetCurrentPage(newCurrentPage);
                }}
                pageClickHandler={() => {
                  onScrollToAlsoSeeContainer();
                }}
              />
            )}
          </SpaceWrapper>
        )}
      </AlsoSeeContent>
    </AlsoSeeContainer>
  );
};

export { AlsoSeeUI };
